import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getNativeBearerAuthHeaders } from '../auth/native-api-auth';
import { getApiBaseUrl } from '../env';

import type {
  AdminLlmModel,
  AdminLlmProfile,
  LlmCallRow,
  LlmCatalogResponse,
  LlmProfileId,
  LlmUsageSummary,
  OpenRouterCatalogMetadata,
} from './llm-models-types';

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getDevAuthHeaders(),
      ...(await getNativeBearerAuthHeaders()),
      ...init?.headers,
    },
  });
}

function parseErrorMessage(text: string, status: number): string {
  try {
    const body = JSON.parse(text) as { error?: { message?: string } };
    if (body.error?.message) {
      return body.error.message;
    }
  } catch {
    // fall through
  }
  return text || `Request failed (${status})`;
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseErrorMessage(text, res.status));
  }
  return res.json() as Promise<T>;
}

export async function lookupAdminLlmModel(
  openrouterModelId: string,
): Promise<OpenRouterCatalogMetadata> {
  const query = encodeURIComponent(openrouterModelId.trim());
  const res = await apiFetch(`/rpc/admin/llm-models/lookup?openrouterModelId=${query}`);
  const data = await parseJson<{ metadata: OpenRouterCatalogMetadata }>(res);
  return data.metadata;
}

export async function fetchAdminLlmModels(): Promise<{
  models: AdminLlmModel[];
  profiles: AdminLlmProfile[];
}> {
  const res = await apiFetch('/rpc/admin/llm-models');
  return parseJson(res);
}

export async function createAdminLlmModel(input: {
  openrouterModelId: string;
  displayName?: string;
  enabled?: boolean;
}): Promise<{
  model: AdminLlmModel;
  profiles: AdminLlmProfile[];
  metadata: OpenRouterCatalogMetadata;
}> {
  const res = await apiFetch('/rpc/admin/llm-models', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return parseJson(res);
}

export async function refreshAdminLlmModelMetadata(
  id: string,
): Promise<{ model: AdminLlmModel; metadata: OpenRouterCatalogMetadata }> {
  const res = await apiFetch(`/rpc/admin/llm-models/${id}/refresh-metadata`, {
    method: 'POST',
  });
  return parseJson(res);
}

export async function patchAdminLlmModel(
  id: string,
  patch: {
    displayName?: string | null;
    enabled?: boolean;
    providerTag?: string | null;
  },
): Promise<AdminLlmModel> {
  const res = await apiFetch(`/rpc/admin/llm-models/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  const data = await parseJson<{ model: AdminLlmModel }>(res);
  return data.model;
}

export async function deleteAdminLlmModel(id: string): Promise<void> {
  const res = await apiFetch(`/rpc/admin/llm-models/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseErrorMessage(text, res.status));
  }
}

export async function assignAdminLlmProfile(
  profile: LlmProfileId,
  modelId: string,
): Promise<AdminLlmProfile[]> {
  const res = await apiFetch(`/rpc/admin/llm-models/profiles/${profile}`, {
    method: 'PUT',
    body: JSON.stringify({ modelId }),
  });
  const data = await parseJson<{ profiles: AdminLlmProfile[] }>(res);
  return data.profiles;
}

export async function fetchAdminLlmCatalog(): Promise<LlmCatalogResponse> {
  const res = await apiFetch('/rpc/admin/llm-models/catalog');
  return parseJson(res);
}

export async function refreshAdminLlmCatalog(): Promise<LlmCatalogResponse> {
  const res = await apiFetch('/rpc/admin/llm-models/catalog/refresh', { method: 'POST' });
  return parseJson(res);
}

export async function fetchAdminLlmUsage(range: {
  from: string;
  to: string;
}): Promise<{ from: string; to: string; summary: LlmUsageSummary }> {
  const params = new URLSearchParams(range);
  const res = await apiFetch(`/rpc/admin/llm-models/usage?${params.toString()}`);
  return parseJson(res);
}

export async function fetchAdminLlmCalls(query: {
  from: string;
  to: string;
  feature?: string;
  model?: string;
  memberId?: string;
  status?: string;
  sort?: 'createdAt' | 'cost';
  dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}): Promise<{ total: number; limit: number; offset: number; calls: LlmCallRow[] }> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value != null && value !== '') params.set(key, String(value));
  }
  const res = await apiFetch(`/rpc/admin/llm-models/calls?${params.toString()}`);
  return parseJson(res);
}

export type {
  AdminLlmModel,
  AdminLlmProfile,
  LlmCallRow,
  LlmCatalogResponse,
  LlmProfileId,
  LlmUsageSummary,
  OpenRouterCatalogMetadata,
};
